// expandable rich text component with animated truncation and "Read more"
// functionality

import { useTheme } from '@repo/theme';
import { AVG_CHAR_WIDTH, calculateTruncatedText, MAX_LINES } from 'component/comment.helpers';
import { RichText } from 'component/richtext';
import { useTranslation } from 'component/translation';
import { useEffect, useRef, useState } from 'react';
import { type LayoutChangeEvent, View } from 'react-native';
import Animated, { useAnimatedProps, useAnimatedStyle, useDerivedValue, useSharedValue, withSpring } from 'react-native-reanimated';
import { getHideFromSnapshotProp } from 'util/snapshotserializer';
import { t } from 'util/stdtypes';

type ExpandableRichTextProps = {
    text: string;
    color?: string;
    onExpand?: () => void;
    disableTruncation?: boolean;
};

export function ExpandableRichText({ text, color, onExpand, disableTruncation = false }: ExpandableRichTextProps) {
    const [truncatedText, setTruncatedText] = useState<string | null>(null);
    const [measurementDone, setMeasurementDone] = useState(false);
    const truncatedHeight = useSharedValue(0);
    const expandedHeight = useSharedValue(0);
    const containerWidthRef = useRef(0);
    const prevTextRef = useRef(text);

    // Re-measure when the text changes (e.g. an edit, or the comment arriving /
    // updating from an async read). Measurement is otherwise done once; without
    // this, a comment whose text changes after first layout keeps its stale
    // truncation (no/incorrect "Read more").
    //
    // Recompute from the last measured container width rather than clearing and
    // waiting for `onLayout` to re-fire: after an in-place text change the
    // container width is unchanged, so `onLayout` may not fire again and the
    // truncation would never be recomputed (the comment renders untruncated).
    useEffect(() => {
        if (prevTextRef.current !== text) {
            prevTextRef.current = text;
            const width = containerWidthRef.current;
            if (width > 0) {
                const charsPerLine = Math.floor(width / AVG_CHAR_WIDTH);
                setTruncatedText(calculateTruncatedText({ maxLines: MAX_LINES, charsPerLine, text }));
                setMeasurementDone(true);
            } else {
                setMeasurementDone(false);
                setTruncatedText(null);
            }
        }
    }, [text]);

    const handleContainerLayout = (event: LayoutChangeEvent) => {
        if (measurementDone) return;
        const width = event.nativeEvent.layout.width;
        if (width > 0) {
            containerWidthRef.current = width;
            const charsPerLine = Math.floor(width / AVG_CHAR_WIDTH);
            setTruncatedText(calculateTruncatedText({ maxLines: MAX_LINES, charsPerLine, text }));
            setMeasurementDone(true);
        }
    };

    if (disableTruncation) {
        return <RichText color={color} text={text} type='large' />;
    }

    if (measurementDone && truncatedText !== null) {
        return (
            <TruncatedView
                color={color}
                expandedHeight={expandedHeight}
                onExpand={onExpand}
                onLayout={handleContainerLayout}
                text={text}
                truncatedHeight={truncatedHeight}
                truncatedText={truncatedText}
            />
        );
    }

    return (
        <View onLayout={handleContainerLayout}>
            <RichText color={color} text={text} type='large' />
        </View>
    );
}

type TruncatedViewProps = {
    text: string;
    truncatedText: string;
    color?: string;
    onExpand?: () => void;
    onLayout: (event: LayoutChangeEvent) => void;
    truncatedHeight: ReturnType<typeof useSharedValue<number>>;
    expandedHeight: ReturnType<typeof useSharedValue<number>>;
};

function TruncatedView({ text, truncatedText, color, onExpand, onLayout, truncatedHeight, expandedHeight }: TruncatedViewProps) {
    const { colors } = useTheme();
    const isExpanded = useSharedValue(false);

    const containerHeight = useDerivedValue(() => {
        if (isExpanded.value) return withSpring(expandedHeight.value);
        return truncatedHeight.value;
    });

    const containerStyle = useAnimatedStyle(() => ({
        height: truncatedHeight.value > 0 ? containerHeight.value : 'auto',
        overflow: isExpanded.value ? 'visible' : truncatedHeight.value > 0 ? 'hidden' : 'visible',
    }));

    const truncatedStyle = useAnimatedStyle(() => ({
        opacity: withSpring(isExpanded.value ? 0 : 1),
        pointerEvents: isExpanded.value ? 'none' : 'auto',
    }));

    const expandedStyle = useAnimatedStyle(() => ({
        opacity: withSpring(isExpanded.value ? 1 : 0),
        pointerEvents: isExpanded.value ? 'auto' : 'none',
    }));

    const truncatedProps = useAnimatedProps(() => ({ 'aria-hidden': isExpanded.value }));
    const expandedProps = useAnimatedProps(() => ({ 'aria-hidden': !isExpanded.value }));

    const handleExpand = () => {
        isExpanded.value = true;
        onExpand?.();
    };

    const measureTruncatedEl = (event: LayoutChangeEvent) => {
        const height = event.nativeEvent.layout.height;
        if (height > 0) truncatedHeight.value = height;
    };

    const measureExpandedEl = (event: LayoutChangeEvent) => {
        const height = event.nativeEvent.layout.height;
        if (height > 0) expandedHeight.value = height;
    };

    const readMoreTranslation = useTranslation(t('comment.read_more_button', 'Read more'));

    const displayText = `${truncatedText}...[${readMoreTranslation}]($readMore)`;

    return (
        <View onLayout={onLayout}>
            <Animated.View style={[{ width: '100%', position: 'relative' }, containerStyle]}>
                <Animated.View
                    animatedProps={truncatedProps}
                    onLayout={measureTruncatedEl}
                    style={truncatedStyle}
                    {...getHideFromSnapshotProp()}
                >
                    <RichText
                        callbackColor={colors.colorTextSecondary}
                        callbacks={{ readMore: handleExpand }}
                        color={color}
                        text={displayText}
                        type='large'
                    />
                </Animated.View>
                <Animated.View
                    animatedProps={expandedProps}
                    onLayout={measureExpandedEl}
                    style={[{ position: 'absolute', top: 0, left: 0, width: '100%' }, expandedStyle]}
                >
                    <RichText color={color} text={text} type='large' />
                </Animated.View>
            </Animated.View>
        </View>
    );
}
